import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Send, RotateCcw, Upload, FileText } from 'lucide-react';
import { uploadFile } from '@/lib/uploadFile';
import { safeDownload } from '@/components/shared/contentHelpers';
import MultiFilePicker from '@/components/shared/MultiFilePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 10;

export default function StudentLessonSubmit({ lesson, token, t, onSubmitted }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleSubmit = async () => {
    if (files.length === 0) {
      toast.error('Adjunta al menos un archivo');
      return;
    }
    setUploading(true);
    try {
      const uploadedFiles = [];
      for (const f of files) uploadedFiles.push(await uploadFile(f, token));
      await axios.post(`${API}/content/submit`, {
        content_id: lesson.id, files: uploadedFiles, links: []
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('submitted'));
      setFiles([]);
      onSubmitted();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || t('error'));
    } finally { setUploading(false); }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/content/cancel-submission/${lesson.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('submission_cancelled'));
      onSubmitted();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || t('error'));
    } finally { setCancelling(false); }
  };

  const submission = lesson.my_submission;
  const isSubmitted = submission && submission.status !== 'cancelled';
  const isCancelled = submission && submission.status === 'cancelled';
  const isResubmitted = submission && submission.status === 'resubmitted';

  if (isSubmitted) {
    return (
      <div className="border-t pt-4 mt-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white ${isResubmitted ? 'bg-amber-500' : 'bg-emerald-500'}`} data-testid={`submission-status-${lesson.id}`}>
            <Send className="w-3 h-3" /> {isResubmitted ? t('resubmitted') : t('submitted')}
          </div>
          {submission.files?.map((f, i) => (
            <button key={i} onClick={() => safeDownload(f.filename, f.original_name, token, t)}
              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              data-testid={`download-submission-${lesson.id}-${i}`}>
              <FileText className="w-3 h-3" /> {f.original_name}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" disabled={cancelling} onClick={handleCancel}
          className="gap-1.5 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600 rounded-lg text-xs"
          data-testid={`cancel-submit-${lesson.id}`}>
          {cancelling ? <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" /> : <><RotateCcw className="w-3 h-3" /> {t('cancel_submission')}</>}
        </Button>
      </div>
    );
  }

  return (
    <div className="border-t pt-4 mt-4 space-y-3" data-testid={`submit-box-${lesson.id}`}>
      <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
        <Upload className="w-4 h-4 text-blue-500" /> {isCancelled ? t('resubmit') : t('submit_activity')}
      </p>
      {isCancelled && <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">{t('submission_cancelled')}</p>}
      <div className="bg-slate-50 rounded-xl p-4 space-y-3">
        <MultiFilePicker
          files={files}
          setFiles={setFiles}
          maxFiles={MAX_FILES}
          testIdPrefix={`submit-files-${lesson.id}`}
          firstButtonLabel="Adjuntar archivo"
          buttonLabel="Adjuntar otro archivo"
        />
        <Button size="sm" disabled={uploading || files.length === 0} onClick={handleSubmit} className="gap-1.5 rounded-lg" data-testid={`submit-btn-${lesson.id}`}>
          {uploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Send className="w-3.5 h-3.5" /> {isCancelled ? t('resubmit') : t('submit_activity')}</>}
        </Button>
      </div>
    </div>
  );
}
